import React from 'react';
import { Mic, MicOff, MessageSquareText, X } from 'lucide-react';
import StudibudiFace from '@/components/brand/StudibudiFace';
import './MorphingPill.css';

export interface MorphingPillProps {
    // State
    agentStatus: 'disconnected' | 'connecting' | 'authenticating' | 'connected' | 'ending' | 'error';
    isUserSpeaking?: boolean;
    isAgentSpeaking?: boolean;
    isUserMuted?: boolean;

    // Actions
    onConnect: () => void;
    onDisconnect: () => void;
    onToggleMute: () => void;
    onToggleTranscript?: () => void;
    isTranscriptVisible?: boolean;
    showTranscriptToggle?: boolean;
    orientation?: 'horizontal' | 'vertical';
    className?: string;
    
    // Background control - set to false on mobile to prevent double dock
    showBackground?: boolean;
    
    // Show "Start Studibudi" text label when disconnected (for mobile)
    showDisconnectedLabel?: boolean;
    
    // Use mobile-optimized control layout with chat icon + mic status capsule
    useMobileControlLayout?: boolean;
}

const MorphingPill: React.FC<MorphingPillProps> = ({
    agentStatus,
    isUserSpeaking: _isUserSpeaking = false,
    isAgentSpeaking: _isAgentSpeaking = false,
    isUserMuted = true,
    onConnect,
    onDisconnect,
    onToggleMute,
    onToggleTranscript,
    isTranscriptVisible = false,
    showTranscriptToggle = true,
    orientation = 'horizontal',
    className = '',
    showBackground = true,
    showDisconnectedLabel = false,
    useMobileControlLayout = false,
}) => {
    const isConnected = agentStatus === 'connected' || agentStatus === 'connecting' || agentStatus === 'authenticating' || agentStatus === 'ending';

    const canToggleTranscript = Boolean(onToggleTranscript && showTranscriptToggle);

    const handleAvatarClick = () => {
        if (canToggleTranscript && !isTranscriptVisible) {
            onToggleTranscript?.();
        }
        if (!isConnected) {
            onConnect();
        }
    };

    return (
        <div
            className={`morphing-pill ${isConnected ? 'connected' : ''} ${orientation === 'vertical' ? 'vertical' : ''} ${className}`}
            style={{
                width: orientation === 'vertical' ? '72px' : undefined,
                transition: 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)',
            }}
        >
            {/* Background & Shadow Layer - hidden on mobile dock via showBackground prop */}
            {showBackground && (
                <div className={`
                    absolute inset-0 -z-10 rounded-[32px] 
                    bg-[#fcfbf9] border border-slate-200/60 
                    shadow-[0_4px_16px_-4px_rgba(0,0,0,0.08)]
                    transition-all duration-700 ease-out
                    ${isConnected ? 'opacity-100' : 'opacity-0'}
                `} />
            )}

            {/* DISCONNECTED: Avatar only (or with label on mobile) */}
            {!isConnected && (
                <button
                    className={`pill-avatar ${showDisconnectedLabel ? 'pill-avatar--with-label' : 'pill-avatar--large'} flex items-center justify-center gap-2 rounded-full hover:bg-slate-50/50 transition-colors`}
                    onClick={handleAvatarClick}
                    data-tour="talk-button"
                    aria-label="Open Studibudi panel and connect"
                    type="button"
                    style={showDisconnectedLabel ? { padding: '8px 16px 8px 12px' } : { width: '52px', height: '52px' }}
                >
                    <StudibudiFace className={showDisconnectedLabel ? "h-7 w-5" : "h-[38px] w-[28px]"} />
                    {showDisconnectedLabel && (
                        <span className="text-sm font-medium text-slate-700 whitespace-nowrap">Start Studibudi</span>
                    )}
                </button>
            )}

            {/* CONNECTED: Mobile layout vs Default layout */}
            {useMobileControlLayout ? (
                /* Mobile layout: Mic status (left) | divider | X (right) - Chat is now separate FAB */
                <div className={`mobile-dock-controls ${!isConnected ? 'hidden' : ''}`}>
                    {/* Mic Status */}
                    <button
                        className={`mobile-dock-btn ${isUserMuted ? 'muted' : 'listening'}`}
                        onClick={onToggleMute}
                        title={isUserMuted ? 'Unmute Microphone' : 'Mute Microphone'}
                        aria-label={isUserMuted ? 'Unmute microphone' : 'Mute microphone'}
                        type="button"
                    >
                        {isUserMuted ? (
                            <MicOff className="h-5 w-5" />
                        ) : (
                            <Mic className="h-5 w-5" />
                        )}
                        <span>{isUserMuted ? 'Mic off' : 'Listening'}</span>
                    </button>
                    
                    {/* Vertical Divider */}
                    <div className="mobile-dock-divider" />
                    
                    {/* Disconnect */}
                    <button
                        className="mobile-dock-btn disconnect"
                        onClick={onDisconnect}
                        title="Disconnect"
                        aria-label="Disconnect from agent"
                        type="button"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            ) : (
                /* Default layout for desktop */
                <div className={`pill-connected ${isConnected ? '' : 'hidden'} ${orientation === 'vertical' ? 'flex-col gap-1' : 'flex-row gap-0.5'}`}>
                    {canToggleTranscript && (
                        <button
                            className="flex items-center justify-center h-12 w-12 mt-1 rounded-full hover:bg-slate-100 transition-colors"
                            onClick={onToggleTranscript}
                            title="Open Transcript"
                            aria-label="Open transcript panel"
                            type="button"
                        >
                            <StudibudiFace className="h-9 w-7" isActive={agentStatus === 'connected'} />
                        </button>
                    )}

                    {/* Mute Toggle */}
                    <button
                        className={`
                            flex items-center justify-center h-10 w-10 rounded-full transition-colors
                            ${!isUserMuted ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}
                        `}
                        onClick={onToggleMute}
                        title={isUserMuted ? 'Unmute' : 'Mute'}
                        aria-label={isUserMuted ? 'Unmute microphone' : 'Mute microphone'}
                        type="button"
                    >
                        {isUserMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                    </button>

                    {/* Divider */}
                    <div className={`${orientation === 'vertical' ? 'w-4 h-px my-0.5' : 'w-px h-4 mx-0.5'} bg-slate-200 opacity-60`} />

                    {/* Disconnect */}
                    <button
                        className="flex items-center justify-center h-10 w-10 rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                        onClick={onDisconnect}
                        title="Disconnect"
                        type="button"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default MorphingPill;
